/**
 * Persist Correspondence Register list filters per sidebar view (all / open / critical / closed)
 * (search, project, status, quick views, saved view, etc.) survive navigation to thread detail and back.
 * Date filters are not persisted — they reset when you leave and return.
 */

const STORAGE_KEY = 'cc_correspondence_register_ui_v1';

export function loadRegisterUiSnapshot(filter) {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    const row = data?.[filter];
    return row && typeof row === 'object' ? row : null;
  } catch {
    return null;
  }
}

export function saveRegisterUiSnapshot(filter, snapshot) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const data = raw ? JSON.parse(raw) : {};
    data[filter] = snapshot;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* quota / private mode */
  }
}

/** Defaults for the correspondence register (no session restore). */
export function getRegisterFilterDefaults(filter) {
  const defaultStatus = filter === 'open' ? 'Open' : filter === 'closed' ? 'Closed' : 'all';
  const defaultPriority = filter === 'critical' ? 'Critical' : 'all';
  return {
    searchQuery: '',
    searchParam: '',
    projectFilter: 'all',
    dateFrom: '',
    dateTo: '',
    debouncedDateFrom: '',
    debouncedDateTo: '',
    dateRangePreset: '',
    typeFilter: 'all',
    categoryFilter: 'all',
    locationBlockFilter: 'all',
    locationBlockSearch: '',
    debouncedLocationBlockSearch: '',
    statusFilter: defaultStatus,
    priorityFilter: defaultPriority,
    registerQuickFilter: 'all',
    savedViewId: null,
    page: 1,
  };
}

export function buildInitialRegisterState(filter) {
  const saved = loadRegisterUiSnapshot(filter);
  const defaults = getRegisterFilterDefaults(filter);
  if (!saved) return defaults;
  return {
    searchQuery: saved.searchQuery ?? defaults.searchQuery,
    searchParam: saved.searchParam ?? defaults.searchParam,
    projectFilter: saved.projectFilter ?? defaults.projectFilter,
    dateFrom: defaults.dateFrom,
    dateTo: defaults.dateTo,
    debouncedDateFrom: defaults.debouncedDateFrom,
    debouncedDateTo: defaults.debouncedDateTo,
    dateRangePreset: defaults.dateRangePreset,
    typeFilter: saved.typeFilter ?? defaults.typeFilter,
    categoryFilter: saved.categoryFilter ?? defaults.categoryFilter,
    locationBlockFilter: saved.locationBlockFilter ?? defaults.locationBlockFilter,
    locationBlockSearch: saved.locationBlockSearch ?? defaults.locationBlockSearch,
    debouncedLocationBlockSearch: saved.debouncedLocationBlockSearch ?? defaults.debouncedLocationBlockSearch,
    statusFilter: saved.statusFilter ?? defaults.statusFilter,
    priorityFilter: saved.priorityFilter ?? defaults.priorityFilter,
    registerQuickFilter: saved.registerQuickFilter ?? defaults.registerQuickFilter,
    savedViewId: saved.savedViewId ?? defaults.savedViewId,
    page: Math.max(1, Number(saved.page) || defaults.page),
  };
}

/** Resolve API status/priority from page scope + filter bar + quick view pill. */
export function resolveRegisterApiFilters(filter, statusFilter, priorityFilter, registerQuickFilter) {
  const pageStatus = filter === 'open' ? 'Open' : filter === 'closed' ? 'Closed' : null;
  const pagePriority = filter === 'critical' ? 'Critical' : null;

  let apiStatus = pageStatus || statusFilter;
  let apiPriority = pagePriority || priorityFilter;

  if (filter === 'all') {
    if (registerQuickFilter === 'open') apiStatus = 'Open';
    else if (registerQuickFilter === 'closed') apiStatus = 'Closed';
    else if (registerQuickFilter === 'critical') apiPriority = 'Critical';
    else if (registerQuickFilter === 'all') {
      apiStatus = statusFilter;
      apiPriority = priorityFilter;
    }
  }

  return { apiStatus, apiPriority };
}
